import Bugsnag from "@bugsnag/js";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type {
  BibleBookId,
  BibleManifest,
  BibleSearchableJson,
} from "@packages/sdk";
import type { DragEventHandler, RefObject } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { getBibleVerseId } from "../../../lib/utils/getBibleVerseId";
import { bibleProperties } from "./analytics.utilities";

const vRegex = /v(\d{2}|\w{3})(\d{3})(\d{3})/;

export const getVerseClass = (el: HTMLElement) => {
  const classes = el.classList;
  let targetVerse: string | null = null;
  for (const c of Array.from(classes)) {
    if (c.startsWith("verse-")) {
      targetVerse = c;
      break;
    }
  }
  return targetVerse;
};
export const getChAndV = (verseClass: string) => {
  const verseId = vRegex.exec(verseClass);
  const chNum = parseInt(verseId?.[2] ?? "");
  const vNum = parseInt(verseId?.[3] ?? "");
  return [chNum, vNum];
};
export const createBeforeMarker = () => {
  const beforeMarker = document.createElement("button");
  beforeMarker.type = "button";
  beforeMarker.classList.add("selected-marker", "before");
  beforeMarker.draggable = true;
  beforeMarker.addEventListener("dragstart", handleDragStart);
  beforeMarker.addEventListener("dragend", handleDragEnd);
  beforeMarker.addEventListener("click", (e) => e.stopPropagation());
  beforeMarker.dataset.position = "start";
  return beforeMarker;
};
export const createAfterMarker = () => {
  const afterMarker = document.createElement("button");
  afterMarker.type = "button";
  afterMarker.classList.add("selected-marker", "after");
  afterMarker.draggable = true;
  afterMarker.addEventListener("dragstart", handleDragStart);
  afterMarker.addEventListener("dragend", handleDragEnd);
  afterMarker.addEventListener("click", (e) => e.stopPropagation());
  afterMarker.dataset.position = "end";
  return afterMarker;
};

export const getText = ({
  searchableJson,
  book,
  nStartChapter = 1,
  nEndChapter = nStartChapter,
  nStartVerse = 1,
  nEndVerse = null,
  maxCharacters = 500,
}: {
  searchableJson: BibleSearchableJson;
  book: BibleBookId;
  nStartChapter?: number;
  nEndChapter?: number;
  nStartVerse?: number;
  nEndVerse?: number;
  maxCharacters?: number;
}): {
  text: string | null;
  shortened: boolean;
  endedEarlyAtChapter?: number;
  endedEarlyAtVerse?: number;
} => {
  const arr: string[] = [];
  let shortened = false;
  let chapterLoop = nStartChapter;
  let verseLoop = nStartVerse;
  let endedEarlyAtChapter: number, endedEarlyAtVerse: number;
  let numCharacters = 0;
  if (!searchableJson?.[book]) return { text: null, shortened: false };
  if (!searchableJson[book][chapterLoop.toString()])
    return { text: null, shortened: false };
  while (chapterLoop <= nEndChapter) {
    while (
      !!searchableJson[book][chapterLoop.toString()][verseLoop.toString()] &&
      (chapterLoop < nEndChapter ||
        (chapterLoop === nEndChapter && (!nEndVerse || verseLoop <= nEndVerse)))
    ) {
      const verse =
        searchableJson[book][chapterLoop.toString()][verseLoop.toString()];
      arr.push(verse);
      numCharacters += verse.length;
      if (maxCharacters !== null && numCharacters > maxCharacters) {
        shortened = true;
        endedEarlyAtChapter = chapterLoop;
        endedEarlyAtVerse = verseLoop;
        break;
      }
      verseLoop++;
    }
    if (shortened) break;
    chapterLoop++;
    verseLoop = 1;
  }
  if (shortened) arr.push("...");

  return {
    text: arr.join(" "),
    shortened,
    endedEarlyAtChapter,
    endedEarlyAtVerse,
  };
};
const handleDragStart = (e: DragEvent) => {
  (e.currentTarget as HTMLElement).classList.add("dragging");
  (e as DragEvent).dataTransfer?.setData(
    (e.currentTarget as HTMLElement).dataset.position ?? "unknown",
    "",
  );
};
const handleDragEnd = (e: DragEvent) => {
  (e.currentTarget as HTMLElement).classList.remove("dragging");
};

export const useBibleSelection = ({
  ref,
  book,
  searchableJson,
  manifestBook,
  manifest,
}: {
  ref: RefObject<HTMLDivElement>;
  book: BibleBookId;
  searchableJson: BibleSearchableJson;
  manifestBook: BibleManifest["books"][0];
  manifest: BibleManifest;
}) => {
  const [selection, setSelection] = useState<{
    chapter: number;
    start: number;
    end: number;
  } | null>(null);
  const tentativeSelection = useRef<typeof selection>(null);
  const [textSelection, setTextSelection] = useState<string | null>(null);
  const { track } = useAnalytics();

  const getVerse = useCallback(
    ({ chapter, verse }: { chapter: number; verse: number }) => {
      if (!ref.current) return null;

      const numericSelector = !!manifestBook?.numeric_id
        ? getBibleVerseId({
            bookNumericId: manifestBook?.numeric_id,
            chapter,
            verse,
          })
        : null;
      const stringSelector = getBibleVerseId({
        book,
        manifest,
        chapter,
        verse,
        preferredVariant: "string",
      });

      let elements: NodeListOf<Element>;

      if (numericSelector) {
        elements = ref.current.querySelectorAll(
          `.verseWrapper.verse-${numericSelector}`,
        );
      }
      if (!elements?.length) {
        elements = ref.current.querySelectorAll(
          `.verseWrapper.verse-${stringSelector}`,
        );
      }
      if (!elements?.length) return null;
      return elements;
    },
    [book, manifest, manifestBook],
  );

  const handleDragEnter = useCallback(
    (e: DragEvent) => {
      // no selection active, ignore the event
      if (!selection || !e.currentTarget) return;

      const overVerse = getVerseClass(e.currentTarget as HTMLElement);
      // not over a proper verse, ignore the event
      if (!overVerse?.length) return;

      const [ch, v] = getChAndV(overVerse);
      // over a verse in a different chapter, ignore the event
      if (ch !== selection.chapter) return;

      const transferType: "start" | "end" | null = (e.dataTransfer
        ?.types?.[0] ?? null) as "start" | "end" | null;
      // invalid drag source, ignore the event
      if (!transferType || !["start", "end"].includes(transferType)) return;

      e.preventDefault();
      tentativeSelection.current = { ...selection };
      if (transferType === "start") {
        tentativeSelection.current.start = v;
        if (v > tentativeSelection.current.end)
          tentativeSelection.current.end = v;
      } else if (transferType === "end") {
        tentativeSelection.current.end = v;
        if (v < tentativeSelection.current.start)
          tentativeSelection.current.start = v;
      }

      Array.from(ref.current.querySelectorAll(".selected")).forEach((v) =>
        v.classList.remove("selected"),
      );
      for (
        let verse = tentativeSelection.current.start;
        verse <= tentativeSelection.current.end;
        verse++
      ) {
        const selectableList = getVerse({
          chapter: tentativeSelection.current.chapter,
          verse: verse,
        });
        selectableList?.forEach((selectable) =>
          selectable.classList?.add("selected"),
        );
      }
    },
    [selection, getVerse],
  );

  const deselect = () => {
    ref.current
      ?.querySelectorAll(".selected")
      ?.forEach((v) => v.classList.remove("selected"));
    ref.current
      ?.querySelectorAll(".selected-marker")
      ?.forEach((m) => m.parentElement.removeChild(m));
  };

  const clearSelection = () => {
    setSelection(null);
    deselect();
    setTextSelection(null);
  };

  const showSelection = useCallback(() => {
    let first: Element, last: Element;
    for (let v = selection.start; v <= selection.end; v++) {
      const wrappers = getVerse({ chapter: selection.chapter, verse: v });
      if (!wrappers?.length) continue;
      if (v === selection.start) {
        first = wrappers.item(0);
      }
      if (v === selection.end) {
        last = wrappers.item(wrappers.length - 1);
      }
      for (const w of Array.from(wrappers)) {
        w.classList.add("selected");
      }
    }

    if (!first || !last) {
      Bugsnag.notify(
        new Error(
          `Version ${manifest.translation_name} (${manifest.translation_id}) ${book} ${selection.chapter}:${selection.start}${selection.end > selection.start ? `-${selection.end}` : ""} cannot be found in HTML`,
        ),
      );
      return;
    }

    first.prepend(createBeforeMarker());
    last.appendChild(createAfterMarker());

    const { text: selectedText } = getText({
      book,
      nEndVerse: selection.end,
      nStartChapter: selection.chapter,
      nStartVerse: selection.start,
      searchableJson,
      maxCharacters: null,
    });

    setTextSelection(selectedText);
  }, [selection, searchableJson, getVerse, book, manifest]);

  const selectionTitle = useMemo(
    () =>
      selection
        ? `${manifestBook.name} ${selection.chapter}:${selection.start}${selection.end > selection.start ? `-${selection.end}` : ""}`
        : null,
    [selection],
  );

  useEffect(() => {
    // clear and re-select everything
    deselect();

    if (selection && !!ref.current) {
      showSelection();
    }
  }, [selection, showSelection]);

  useLayoutEffect(() => {
    // this intentionally has no dependency array because we want to run it anytime this component renders
    // this is idempotent because dangerouslySetInnerHTML is dynamic, not static -- it will rerender even if
    // the body text does not change on component rerenders, and when that happens, all of our inserts and
    // modifications are lost
    if (selection) {
      showSelection();
    }

    const verses = ref.current?.querySelectorAll(".verseWrapper");
    if (verses) {
      verses.forEach((v) => v.addEventListener("dragover", handleDragEnter));

      return () => {
        if (verses) {
          verses.forEach((v) =>
            v?.removeEventListener("dragover", handleDragEnter),
          );
        }
      };
    }
  });

  const handleDrop: DragEventHandler = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (
        !selection ||
        !tentativeSelection.current ||
        (selection.chapter === tentativeSelection.current.chapter &&
          selection.start === tentativeSelection.current.start &&
          selection.end === tentativeSelection.current.end)
      )
        return;

      const newSelection = { ...tentativeSelection.current };
      e.dataTransfer?.clearData();
      setSelection(newSelection);
    },
    [selection],
  );

  const handleClickForSelection = useCallback(
    (verseWrapper: HTMLElement) => {
      const targetVerse = getVerseClass(verseWrapper);
      if (!targetVerse?.length) return;

      const [chNum, vNum] = getChAndV(targetVerse);
      if (isNaN(chNum) || isNaN(vNum)) return;

      const newSelection =
        !!selection && selection.chapter === chNum
          ? { ...selection }
          : { chapter: chNum, start: vNum, end: vNum };

      if (!!selection && selection.chapter === chNum) {
        if (vNum < selection.start) {
          newSelection.start = vNum;
        } else if (
          vNum === selection.start &&
          selection.end > selection.start
        ) {
          // skip verses missing from the source material (e.g., John 5:4)
          let next = selection.start + 1;
          while (
            next < selection.end &&
            !getVerse({ chapter: chNum, verse: next })
          ) {
            next++;
          }
          newSelection.start = next;
        } else if (vNum === selection.end && selection.end > selection.start) {
          let prev = selection.end - 1;
          while (
            prev > selection.start &&
            !getVerse({ chapter: chNum, verse: prev })
          ) {
            prev--;
          }
          newSelection.end = prev;
        } else if (vNum === selection.start && vNum === selection.end) {
          clearSelection();
          return;
        } else {
          newSelection.end = vNum;
        }
      }

      if (!!selection) {
        void track({
          event: HallowAnalyticsEvent.SelectedTextChanged,
          properties: bibleProperties({
            screen_name: "bible",
            translation_id: manifest.translation_id,
            translation_name: manifest.translation_name,
            book_id: book,
            chapter: chNum,
            verse_start: newSelection.start,
            verse_end: newSelection.end,
            type: "notation",
          }),
        });
      } else {
        void track({
          event: HallowAnalyticsEvent.TextSelected,
          properties: bibleProperties({
            screen_name: "bible",
            translation_id: manifest.translation_id,
            translation_name: manifest.translation_name,
            book_id: book,
            chapter: chNum,
            verse_start: newSelection.start,
            verse_end: newSelection.end,
            type: "notation",
          }),
        });
      }

      setSelection(newSelection);
    },
    [selection, manifest, getVerse],
  );

  return {
    handleDrop,
    handleClickForSelection,
    selection,
    textSelection,
    clearSelection,
    selectionTitle,
  };
};

export enum HighlightColors {
  Yellow = 1,
  Pink = 2,
  Aqua = 3,
  Green = 4,
  Blue = 5,
  Purple = 6,
  Red = 7,
  Orange = 8,
}
